/**
 * Evolução do ranking: posição por dia (1 em cima), pódio final + você.
 *
 * SVG desenhado aqui mesmo (sem biblioteca): linhas finas, marcadores de
 * 8 px, rótulo direto no fim de cada linha, legenda sempre presente, um
 * tooltip por dia ao tocar, e uma tabela para quem prefere ler. As cores
 * são uma ordem fixa por pessoa (não pelo lugar), validadas para daltonismo
 * em claro e escuro.
 */
import { formatMoney } from '@nv/core';
import { useId, useState } from 'react';

import { useTrip } from '../../trip/TripContext';
import { Card } from '../../ui/primitives';
import { useEvolucaoDoRanking } from './api';

const ddmm = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
const primeiro = (n: string) => n.trim().split(/\s+/)[0] ?? n;

export function EvolucaoDoRanking() {
  const { tripId } = useTrip();
  const historico = useEvolucaoDoRanking(tripId);
  const [dia, setDia] = useState<number | null>(null);
  const [tabela, setTabela] = useState(false);
  const id = useId();
  const h = historico.data;
  if (!h || h.days.length < 2 || h.series.length === 0) return null;

  const W = 340;
  const H = 160;
  const padL = 26;
  const padR = 78;
  const padT = 12;
  const padB = 26;
  const n = h.days.length;
  const posMax = Math.max(2, h.people);
  const x = (i: number) => padL + (i * (W - padL - padR)) / (n - 1);
  const y = (p: number) => padT + ((p - 1) * (H - padT - padB)) / (posMax - 1);
  const passoX = Math.max(1, Math.ceil(n / 6));
  // Rótulos diretos no fim das linhas: duas pessoas empatadas terminam na
  // mesma altura — afasta os rótulos 11 px um do outro, mantendo a ordem.
  const yRotulo = (() => {
    const ordem = h.series.map((s, k) => ({ k, y: y(s.positions[n - 1] ?? 1) })).sort((a, b) => a.y - b.y);
    for (let i = 1; i < ordem.length; i++) if (ordem[i]!.y - ordem[i - 1]!.y < 11) ordem[i]!.y = ordem[i - 1]!.y + 11;
    const mapa = new Map(ordem.map((o) => [o.k, o.y]));
    return (k: number) => mapa.get(k) ?? 0;
  })();

  return (
    <Card className="rk__evo" data-testid="rk-evolucao">
      <div className="rk__home-cab">
        <span className="rk__linha-nome">Posição por dia</span>
        <button type="button" className="nv-btn nv-btn--chip" onClick={() => setTabela((v) => !v)} aria-pressed={tabela}>
          {tabela ? 'Ver gráfico' : 'Ver tabela'}
        </button>
      </div>

      {!tabela && (
        <>
          <svg
            viewBox={`0 0 ${W} ${H}`}
            className="rk__svg"
            role="img"
            aria-labelledby={`${id}-t`}
            onPointerMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              const px = ((e.clientX - r.left) / r.width) * W;
              const i = Math.round(((px - padL) / (W - padL - padR)) * (n - 1));
              setDia(Math.max(0, Math.min(n - 1, i)));
            }}
            onPointerLeave={() => setDia(null)}
          >
            <title id={`${id}-t`}>Posição no ranking por dia da viagem</title>
            {Array.from({ length: posMax }, (_, k) => k + 1).map((p) => (
              <g key={p}>
                <line x1={padL} x2={W - padR} y1={y(p)} y2={y(p)} className="rk__grid" />
                <text x={padL - 6} y={y(p) + 3} className="rk__eixo" textAnchor="end">{p}º</text>
              </g>
            ))}
            {h.days.map((d, i) => (i % passoX === 0 || i === n - 1) && (
              <text key={d} x={x(i)} y={H - 8} className="rk__eixo" textAnchor="middle">{ddmm(d)}</text>
            ))}
            {dia !== null && <line x1={x(dia)} x2={x(dia)} y1={padT} y2={H - padB} className="rk__cursor" />}
            {h.series.map((s, k) => (
              <g key={s.memberId} className={`rk__serie rk__serie--${k + 1}`}>
                <polyline points={s.positions.map((p, i) => `${x(i)},${y(p)}`).join(' ')} className="rk__linha-svg" />
                {s.positions.map((p, i) => <circle key={i} cx={x(i)} cy={y(p)} r={dia === i ? 5 : 3.5} className="rk__ponto" />)}
                <text x={x(n - 1) + 8} y={yRotulo(k) + 4} className="rk__rotulo">{primeiro(s.name)}{s.me ? ' (você)' : ''}</text>
              </g>
            ))}
          </svg>
          {dia !== null && (
            <div className="rk__tooltip" role="status">
              <b>{ddmm(h.days[dia]!)}</b>
              {h.series.map((s) => (
                <span key={s.memberId}> · {primeiro(s.name)} {s.positions[dia]}º ({formatMoney(s.totalsMinor[dia] ?? 0, h.currency, { hideCents: true })})</span>
              ))}
            </div>
          )}
          <ul className="rk__legenda" aria-label="Legenda">
            {h.series.map((s, k) => (
              <li key={s.memberId} className={`rk__serie--${k + 1}`}><span className="rk__legenda-cor" aria-hidden="true" />{primeiro(s.name)}{s.me ? ' (você)' : ''}</li>
            ))}
          </ul>
        </>
      )}

      {tabela && (
        <div className="rk__tabela-wrap">
          <table className="rk__tabela">
            <thead>
              <tr><th>Dia</th>{h.series.map((s) => <th key={s.memberId}>{primeiro(s.name)}{s.me ? ' (você)' : ''}</th>)}</tr>
            </thead>
            <tbody>
              {h.days.map((d, i) => (
                <tr key={d}>
                  <td className="mono">{ddmm(d)}</td>
                  {h.series.map((s) => <td key={s.memberId}>{s.positions[i]}º · <span className="mono">{formatMoney(s.totalsMinor[i] ?? 0, h.currency, { hideCents: true })}</span></td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
